import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDarkMode } from "../contexts/DarkModeContext";
import OpenInTabButton from "./OpenInTabButton";

// Internal storage backends serve manually-uploaded files; surface them as
// "knowledge library" rather than the underlying tech name.
const PROVIDER_LABELS = {
  s3: "your S3 bucket",
  gcs: "your GCS bucket",
  google_drive: "Google Drive",
  rustfs: "the knowledge library",
  minio: "the knowledge library",
};

function describeFullDocSource(source, provider) {
  const label = PROVIDER_LABELS[provider] || provider || "storage";
  if (source === "proxy") return `via Discover (${label})`;
  return label;
}

// Modal that shows a server-rendered PNG of the cited PDF page with the chunk
// region highlighted in yellow. The full document opens via the cloud signed
// URL when the user clicks "Open full document". The highlight is just a
// preview — the actual file is served direct from the user's storage.
export default function HighlightPreviewModal({
  highlightUrl,
  fullDocUrl,
  proxyUrl,
  docName,
  source,
  provider,
  authHeader,
  onClose,
}) {
  const { isDarkMode } = useDarkMode();
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [errMsg, setErrMsg] = useState("");

  // Pre-fetch the highlight PNG into a blob so we can show error state if it
  // fails (rather than the broken-image icon) and so the <img> doesn't flicker.
  const [imgSrc, setImgSrc] = useState("");
  useEffect(() => {
    let cancelled = false;
    let createdUrl = "";
    (async () => {
      try {
        const res = await fetch(highlightUrl, {
          credentials: "include",
          headers: authHeader ? { Authorization: authHeader } : undefined,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setImgSrc(createdUrl);
        setStatus("ready");
      } catch (e) {
        if (!cancelled) {
          setStatus("error");
          setErrMsg(e?.message || String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [highlightUrl, authHeader]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const sourceLabel = describeFullDocSource(source, provider);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998] transition-opacity"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Highlighted excerpt from ${docName}`}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          className={`relative w-full max-w-4xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col ${
            isDarkMode ? "bg-slate-800" : "bg-white"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-5 py-3 border-b ${
            isDarkMode ? "border-slate-700" : "border-gray-200"
          }`}>
            <div className="flex-1 mr-4 min-w-0">
              <h3 className={`text-sm font-semibold truncate ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}>{docName}</h3>
              <div className={`text-xs mt-0.5 truncate ${
                isDarkMode ? "text-slate-400" : "text-gray-500"
              }`}>
                Highlighted excerpt &middot; full document hosted on {sourceLabel}
              </div>
              {(fullDocUrl || proxyUrl) && (
                <a
                  href={fullDocUrl || proxyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={fullDocUrl || proxyUrl}
                  className={`block text-[11px] mt-0.5 truncate font-mono underline ${
                    isDarkMode ? "text-sky-400 hover:text-sky-300" : "text-sky-600 hover:text-sky-700"
                  }`}
                >
                  {fullDocUrl || proxyUrl}
                </a>
              )}
            </div>
            <OpenInTabButton
              externalUrl={fullDocUrl}
              proxyUrl={proxyUrl}
              authHeader={authHeader}
              label="Open full document ↗"
              className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors mr-2 disabled:opacity-60 ${
                isDarkMode
                  ? "bg-sky-600 hover:bg-sky-500 text-white"
                  : "bg-sky-600 hover:bg-sky-700 text-white"
              }`}
            />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`p-1 rounded-lg transition-colors ${
                isDarkMode
                  ? "hover:bg-slate-700 text-slate-400 hover:text-slate-200"
                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Body */}
          <div className="overflow-auto p-4 flex-1 flex items-center justify-center min-h-[200px]">
            {status === "loading" && (
              <div className={`text-sm ${isDarkMode ? "text-slate-400" : "text-gray-600"}`}>
                Rendering excerpt…
              </div>
            )}
            {status === "error" && (
              <div className="text-red-600 text-sm text-center p-4">
                Couldn't render the highlight.{" "}
                <OpenInTabButton
                  externalUrl={fullDocUrl}
                  proxyUrl={proxyUrl}
                  authHeader={authHeader}
                  label="Open the full document instead"
                  className="underline"
                />
                .
                {errMsg && <div className="text-xs mt-2 opacity-70 break-all">{errMsg}</div>}
              </div>
            )}
            {status === "ready" && (
              <img
                src={imgSrc}
                alt={`Highlighted excerpt from ${docName}`}
                className="max-w-full max-h-[75vh] object-contain rounded shadow-md"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

HighlightPreviewModal.propTypes = {
  highlightUrl: PropTypes.string.isRequired,
  fullDocUrl: PropTypes.string,
  proxyUrl: PropTypes.string,
  docName: PropTypes.string.isRequired,
  source: PropTypes.string,
  provider: PropTypes.string,
  authHeader: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
